import {Dispatch, SetStateAction, useCallback, useEffect} from 'react';
import useLocalStorageWithEvents from './useLocalStorageWithEvents.ts';
import {useSearchParams} from 'react-router-dom';
import {isSameTokenSession} from '../../appUtils/authentication/tokenRefreshUtils.ts';
import {
  makeEmptyToken,
  parseStoredToken,
  TokenType,
} from 'appUtils/authentication/authenticationUtils.ts';

export type SetTokenType = Dispatch<SetStateAction<TokenType>>;

/**
 * Hook that provides access to JWT token value and a way to set a new token.
 */
export function useToken(
  initialValue: TokenType = {
    raw: undefined,
    context: 'unknown',
    contextLog: [],
  },
): [TokenType, SetTokenType] {
  // Parse the token from the URL or failing that the LocalStorage value stored in appProps.token
  if (process.env.REACT_APP_FORCED_TOKEN !== undefined) {
    const token = {
      raw: process.env.REACT_APP_FORCED_TOKEN,
      context: 'env',
      contextLog: ['env'],
    };
    // Dummy function as the token is hardcoded
    const setToken: SetTokenType = (_value) => {};
    return [token, setToken];
  } else {
    const [storedTokenData, localStorageSetToken] = useLocalStorageWithEvents<
      string | undefined
    >('token', initialValue.raw);
    const storedToken = parseStoredToken(storedTokenData);
    const [searchParams, setSearchParams] = useSearchParams();
    let queryToken: string | null = null;
    let context = 'none';
    if (searchParams.has('token')) {
      queryToken = searchParams.get('token');
      context = 'query';
    }
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('useToken parseStoredToken()', {storedToken, queryToken});
    }

    // Re-opening a login link keeps the stored token if it already renews the link's session
    const storedRenewsQueryToken =
      !!queryToken &&
      !!storedToken &&
      isSameTokenSession(queryToken, storedToken, 'useToken');
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('useToken', {hasQueryToken: !!queryToken, storedRenewsQueryToken});
    }

    const setToken: SetTokenType = useCallback((value: SetStateAction<TokenType>) => {
      const newValue =
        value instanceof Function
          ? value(
              storedToken
                ? {
                    raw: storedToken,
                    context: 'local-storage',
                    contextLog: ['local-storage'],
                  }
                : makeEmptyToken('local-storage'),
            )
          : value;
      if (process.env.REACT_AUTH_DEBUG && console.log) {
        console.log('useToken.setToken()', {
          raw: newValue.raw,
          context: newValue.context,
          contextLog: newValue.contextLog,
        });
      }
      localStorageSetToken(newValue.raw);
    }, []);

    // If the query contains a token parameter store it in local storage and then redirect to a url without it
    useEffect(() => {
      if (queryToken) {
        if (queryToken !== storedToken && !storedRenewsQueryToken) {
          // token that is taken from query is not yet validated
          localStorageSetToken(queryToken);
        }
        searchParams.delete('token');
        setSearchParams(searchParams);
      }
    }, [queryToken, storedToken, storedRenewsQueryToken]);

    if (!queryToken || storedRenewsQueryToken) {
      context = 'local-storage';
    }
    const tokenResult = {
      raw: !!queryToken && !storedRenewsQueryToken ? queryToken : storedToken,
      context: context,
      contextLog: [context],
    };
    if (process.env.REACT_AUTH_DEBUG && console.log) {
      console.log('tokenResult', tokenResult);
    }
    return [tokenResult, setToken];
  }
}
